/** Every staff list keeps its page, sort and filters in the browser URL, and
 *  the URL's parameters pass straight through to the API. These helpers are
 *  the only place that reads or rewrites them. */

export type SortDir = "asc" | "desc";

/** The URL's parameters as the API should receive them. includeHidden is IT's
 *  alone: a shared link carrying it must not turn Mark's page into a 403. */
export function apiParams(url: URLSearchParams, canSeeHidden: boolean): URLSearchParams {
  const out = new URLSearchParams(url);
  if (!canSeeHidden || out.get("includeHidden") !== "true") out.delete("includeHidden");
  return out;
}

/** A copy of `url` with each key set, or deleted when its value is null. */
export function withParams(url: URLSearchParams, changes: Record<string, string | null>): URLSearchParams {
  const out = new URLSearchParams(url);
  for (const [key, value] of Object.entries(changes)) {
    if (value === null) out.delete(key);
    else out.set(key, value);
  }
  return out;
}

export function readPage(url: URLSearchParams, name = "page"): number {
  const value = url.get(name);
  return value !== null && /^[1-9]\d*$/.test(value) ? Number(value) : 1;
}

export function readDir(url: URLSearchParams, name = "dir"): SortDir {
  return url.get(name) === "desc" ? "desc" : "asc";
}

/** Clicking a column heading: a new column sorts ascending; the same column
 *  reverses. Either way the list goes back to page 1. A URL with no sort
 *  parameter is on the list's `defaultSort` (ascending), which its table shows
 *  as sorted, so the first click on that column reverses it. */
export function toggleSort(
  url: URLSearchParams,
  key: string,
  names: { sort: string; dir: string; page: string } = { sort: "sort", dir: "dir", page: "page" },
  defaultSort?: string
): URLSearchParams {
  const same = (url.get(names.sort) ?? defaultSort) === key;
  const dir: SortDir = same && readDir(url, names.dir) === "asc" ? "desc" : "asc";
  return withParams(url, { [names.sort]: key, [names.dir]: dir, [names.page]: null });
}
